import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Replay as ReconnectIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import SidebarNav from './components/SidebarNav';
import WorkspaceSwitcher from './components/WorkspaceSwitcher';
import { getColors } from './theme/tokens';
import { ConnectionStatus } from '../services/WebSocketService';

const SIDEBAR_WIDTH = 224;

export interface AppShellProps {
  mode: 'light' | 'dark';
  toggleColorMode: () => void;
  wsStatus: ConnectionStatus;
  onReconnect: () => void;
  userName?: string;
  /** Initial data load in progress — drives the thin top progress bar. */
  loading?: boolean;
  /** Chunked-load progress; when present the top bar is determinate. */
  loadingProgress?: { current: number; total: number } | null;
  /** Slot rendered in the top bar between the brand and the right controls. */
  topbarCenter?: React.ReactNode;
  children: React.ReactNode;
}

const statusMeta = (status: ConnectionStatus) => {
  switch (status) {
    case 'connected':
      return { label: 'Live', hue: '#1aae39' };
    case 'connecting':
      return { label: 'Connecting', hue: '#dd5b00' };
    default:
      return { label: 'Offline', hue: '#d93a3a' };
  }
};

const AppShell: React.FC<AppShellProps> = ({
  mode,
  toggleColorMode,
  wsStatus,
  onReconnect,
  userName,
  loading,
  loadingProgress,
  topbarCenter,
  children,
}) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const status = statusMeta(wsStatus);
  const hasProgress = !!loadingProgress && loadingProgress.total > 0;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: c.canvas }}>
      {/* Sidebar */}
      <Box
        component="aside"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          borderRight: `1px solid ${c.hairline}`,
          backgroundColor: c.canvasSoft,
          position: 'sticky',
          top: 0,
          height: '100vh',
        }}
      >
        {/* Brand / workspace switcher */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2.5, pt: 2, pb: 1 }}>
          <WorkspaceSwitcher />
        </Box>

        <SidebarNav />

        <Box sx={{ flexGrow: 1 }} />

        {/* Footer: user */}
        {userName && (
          <Box sx={{ px: 2, pb: 2 }}>
            <Divider sx={{ borderColor: c.hairline, mb: 1.5 }} />
            <Typography
              variant="caption"
              sx={{ color: c.inkFaint, display: 'block', px: 0.5 }}
              noWrap
            >
              {userName}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Main column */}
      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Top bar */}
        <Box
          component="header"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 3,
            height: 52,
            borderBottom: `1px solid ${c.hairline}`,
            backgroundColor: c.canvas,
            position: 'sticky',
            top: 0,
            zIndex: theme.zIndex.appBar,
          }}
        >
          {/* Mobile brand (sidebar hidden) */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
            <WorkspaceSwitcher compact />
          </Box>

          <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', justifyContent: 'center' }}>
            {topbarCenter}
          </Box>

          {/* Connection pill */}
          <Chip
            icon={
              wsStatus === 'connecting' ? (
                <CircularProgress size={10} sx={{ color: `${status.hue} !important`, ml: 0.75 }} />
              ) : (
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    bgcolor: status.hue,
                    ml: 1,
                  }}
                />
              )
            }
            label={status.label}
            size="small"
            sx={{
              backgroundColor: 'transparent',
              border: `1px solid ${c.hairline}`,
              color: c.inkMuted,
              fontWeight: 600,
              fontSize: '0.72rem',
              '& .MuiChip-icon': { mr: -0.5 },
            }}
          />
          {(wsStatus === 'disconnected' || wsStatus === 'error') && (
            <Tooltip title="Reconnect" arrow>
              <IconButton size="small" onClick={onReconnect}>
                <ReconnectIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title={mode === 'dark' ? 'Light mode' : 'Dark mode'} arrow>
            <IconButton size="small" onClick={toggleColorMode} data-testid="v2-theme-toggle">
              {mode === 'dark' ? (
                <LightModeIcon sx={{ fontSize: 18 }} />
              ) : (
                <DarkModeIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>

          {/* Thin load bar riding the header's bottom edge during data fetch. */}
          {loading && (
            <LinearProgress
              variant={hasProgress ? 'determinate' : 'indeterminate'}
              value={
                hasProgress
                  ? Math.min(100, (loadingProgress!.current / loadingProgress!.total) * 100)
                  : undefined
              }
              aria-label="Loading deployments"
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: -1,
                height: 2,
                backgroundColor: 'transparent',
                '& .MuiLinearProgress-bar': { backgroundColor: c.primary },
              }}
            />
          )}
        </Box>

        {/* Mobile nav strip */}
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            borderBottom: `1px solid ${c.hairline}`,
            backgroundColor: c.canvasSoft,
            overflowX: 'auto',
          }}
        >
          <SidebarNav />
        </Box>

        {/* Page content */}
        <Box component="main" sx={{ flexGrow: 1, px: { xs: 2, md: 4 }, py: 3, minWidth: 0 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AppShell;
